// Photos en double (demande d'Alexandra du 24/10/2026) : une photo déjà ajoutée au même endroit
// (inventaire, emplacement du tour, intervention) n'est pas reprise, et on le dit.
// Empreinte : SHA-256 des premiers 256 Ko du fichier et sa taille quand le navigateur le permet, c'est-à-dire
// en https (lire une photo de téléphone en entier, pour toute une sélection, saturait la mémoire d'un iPhone) ;
// sinon le nom, la taille et le type du fichier (test sur le wifi local, en http).

const FINGERPRINT_BYTES = 256 * 1024

function toHex(buffer)
{
	return Array.from(new Uint8Array(buffer)).map(byte => byte.toString(16).padStart(2, '0')).join('')
}

function readBuffer(blob)
{
	if (typeof blob.arrayBuffer === 'function')
		return blob.arrayBuffer()
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(reader.result)
		reader.onerror = () => reject(reader.error)
		reader.readAsArrayBuffer(blob)
	})
}

export function fileKey(file)
{
	return 'file:' + (file.name || '') + '|' + (file.size || 0) + '|' + (file.type || '')
}

export function photoFingerprint(file)
{
	const subtle = typeof window !== 'undefined' && window.crypto && window.crypto.subtle
	if (!subtle)
		return Promise.resolve(fileKey(file))
	return readBuffer(file.slice(0, FINGERPRINT_BYTES))
		.then(buffer => subtle.digest('SHA-256', buffer))
		.then(digest => 'sha256:' + toHex(digest) + ':' + (file.size || 0))
		.catch(() => fileKey(file))
}

// Réduction d'une photo avant envoi. Une photo d'iPhone fait 3 à 8 Mo (12 à 48 Mpx) : envoyée telle quelle, elle
// occupait le réseau mobile, la mémoire du téléphone et le serveur pour rien. On l'envoie en JPEG de 1600 px
// au plus (≈ 150 à 350 Ko, de quoi passer sur un réseau mobile faible) et on garde une miniature de 720 px pour l'affichage sur le téléphone.
// L'orientation de la photo est appliquée (le navigateur la lit dans l'image) ; en cas d'échec, la photo
// d'origine est envoyée telle quelle.
export const UPLOAD_SIDE = 1600
export const PREVIEW_SIDE = 720
const UPLOAD_QUALITY = 0.75
const PREVIEW_QUALITY = 0.8
const KEEP_BELOW_BYTES = 250 * 1024

function loadImage(file)
{
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const image = new Image()
		const release = () => {
			image.onload = null
			image.onerror = null
			image.src = ''
			URL.revokeObjectURL(url)
		}
		image.onload = () => resolve({ image, width: image.naturalWidth, height: image.naturalHeight, release })
		image.onerror = () => {
			release()
			reject(new Error('image'))
		}
		image.src = url
	})
}

function drawToBlob(image, width, height, side, quality)
{
	const scale = Math.min(1, side / Math.max(width, height))
	const canvas = document.createElement('canvas')
	canvas.width = Math.max(1, Math.round(width * scale))
	canvas.height = Math.max(1, Math.round(height * scale))
	const context = canvas.getContext('2d')
	context.fillStyle = '#FFFFFF'
	context.fillRect(0, 0, canvas.width, canvas.height)
	context.drawImage(image, 0, 0, canvas.width, canvas.height)
	return new Promise((resolve, reject) => {
		canvas.toBlob(blob => {
			canvas.width = 0
			canvas.height = 0
			if (blob)
				resolve(blob)
			else
				reject(new Error('blob'))
		}, 'image/jpeg', quality)
	})
}

function jpegName(file)
{
	const base = (file.name || 'photo').replace(/\.[^.]*$/, '') || 'photo'
	return base + '.jpg'
}

// Résout { file, preview } : file est ce qu'on envoie, preview (Blob de 720 px, ou null) ce qu'on affiche.
// Une à la fois (le décodage d'une photo de 12 Mpx coûte ≈ 48 Mo de mémoire).
export function prepareImage(file)
{
	if (typeof document === 'undefined' || !file || !/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type || ''))
		return Promise.resolve({ file, preview: null })
	let loaded = null
	return loadImage(file)
		.then(result => {
			loaded = result
			const { image, width, height } = result
			const resize = width > UPLOAD_SIDE || height > UPLOAD_SIDE || file.size > KEEP_BELOW_BYTES
			const upload = resize ? drawToBlob(image, width, height, UPLOAD_SIDE, UPLOAD_QUALITY) : Promise.resolve(null)
			return upload.then(blob => drawToBlob(image, width, height, PREVIEW_SIDE, PREVIEW_QUALITY).then(preview => ({
				file: blob ? new File([blob], jpegName(file), { type: 'image/jpeg', lastModified: file.lastModified || Date.now() }) : file,
				preview
			})))
		})
		.catch(() => ({ file, preview: null }))
		.then(prepared => {
			if (loaded)
				loaded.release()
			return prepared
		})
}

// candidates : [{ file, fingerprint }] ; known : empreintes déjà présentes. Une photo choisie deux
// fois dans la même sélection compte aussi comme doublon.
export function splitDuplicates(candidates, known)
{
	const seen = new Set((known || []).filter(Boolean))
	const fresh = []
	let duplicates = 0
	candidates.forEach(candidate => {
		if (seen.has(candidate.fingerprint))
			duplicates++
		else
		{
			seen.add(candidate.fingerprint)
			fresh.push(candidate)
		}
	})
	return { fresh, duplicates }
}

export function duplicateNotice(count)
{
	if (!count)
		return null
	if (count === 1)
		return 'Cette photo est déjà ajoutée : elle n\'a pas été reprise.'
	return count + ' photos sont déjà ajoutées : elles n\'ont pas été reprises.'
}

export function fingerprintsOf(photos)
{
	return (photos || []).map(photo => photo && photo.fingerprint).filter(Boolean)
}
